// ============================================================
// PIE — useMediaFacts (puente DOM → SubjectFacts)
// ============================================================
// Extensión hacia afuera (P6): el núcleo puro no sabe qué es un
// <img> ni un canvas; este hook vive en la capa de presentación y
// traduce el DOM real a señales para los analyzers.
//
// FASES (P10 — Visual Stability):
//   'baseline' → primera pintura, SOLO facts de contenido (decisión
//                ≡ VCE actual — idéntico a hoy, cero riesgo).
//   'measured' → onLoad: dimensiones reales (naturalWidth/Height) +
//                letterbox (canvas sampling presupuestado) → la
//                decisión final se re-optimiza UNA vez y se estabiliza.
//
// GARANTÍAS:
//   - Una sola transición por tarjeta (flag measuredRef).
//   - Imagen en caché → facts completos al montar (sin esperar onLoad).
//   - onerror → degradación elegante (P7): se queda en baseline.
//   - letterbox diferido (requestIdleCallback) + presupuesto ms.
// ============================================================
import { useEffect, useRef, useState } from 'react';
import type { SubjectFacts, VisualItem } from './types';
import { dimensionsAnalyzer, letterboxAnalyzer } from './analyzers';

export interface MediaFactsOptions {
  letterbox?: boolean;          // default true (I2: informa, no bloquea)
  letterboxBudgetMs?: number;   // default 8ms por imagen
  /** Analizadores perceptuales extra (visión futura) — se fusionan */
  extraAnalyzers?: Array<{ id: string; analyze: () => Partial<SubjectFacts> }>;
}

export type MediaFactsPhase = 'baseline' | 'measured';

export interface MediaFactsResult {
  facts: Partial<SubjectFacts>;
  phase: MediaFactsPhase;
}

/** Señales para el analyzer de dimensiones (puras — el hook solo mide). */
function readImageSize(img: HTMLImageElement): Partial<SubjectFacts> {
  if (!img.complete || !img.naturalWidth) return {};
  return dimensionsAnalyzer.analyze({
    items: [],
    signals: { width: img.naturalWidth, height: img.naturalHeight },
  }) as Partial<SubjectFacts>;
}

/** Muestreo de bordes vía canvas 8×8 (presupuestado). Puro de efecto. */
function sampleEdges(img: HTMLImageElement, budgetMs: number): Array<[number, number, number]> | null {
  const t0 = performance.now();
  try {
    const c = document.createElement('canvas');
    c.width = 8;
    c.height = 8;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, 8, 8);
    const data = ctx.getImageData(0, 0, 8, 8).data;
    // 4 esquinas + 4 centros de borde (píxeles 8×8 → coordenadas discretas)
    const coords: Array<[number, number]> = [
      [0, 0], [7, 0], [0, 7], [7, 7],           // esquinas
      [3, 0], [4, 7], [0, 3], [7, 4],           // centros de borde
    ];
    const samples: Array<[number, number, number]> = [];
    for (const [x, y] of coords) {
      const i = (y * 8 + x) * 4;
      samples.push([data[i], data[i + 1], data[i + 2]]);
    }
    if (performance.now() - t0 > budgetMs) return null; // presupuesto excedido
    return samples;
  } catch {
    return null; // canvas bloqueado (CORS/privacidad) → degradación elegante
  }
}

export function useMediaFacts(
  items: VisualItem[],
  containerRef: React.RefObject<HTMLElement | null>,
  opts: MediaFactsOptions = {},
): MediaFactsResult {
  const { letterbox = true, letterboxBudgetMs = 8 } = opts;
  const [phase, setPhase] = useState<MediaFactsPhase>('baseline');
  const [facts, setFacts] = useState<Partial<SubjectFacts>>({});
  const measuredRef = useRef(false);

  useEffect(() => {
    // Nueva tarjeta / nuevos items → reset a baseline (P10: pintura estable inicial)
    measuredRef.current = false;
    setPhase('baseline');
    setFacts({});
  }, [items]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || measuredRef.current) return;
    const img = el.querySelector('img');
    if (!img) return;

    const settle = (dimensions: Partial<SubjectFacts>) => {
      if (measuredRef.current) return; // UNA sola transición (P10)
      measuredRef.current = true;

      // Perceptual heurísticos (I2 — derivados de physical; visión mañana)
      const perceptual: Partial<SubjectFacts> = {};
      if (dimensions.realAspect) {
        perceptual.visualOccupancy = Math.min(1, dimensions.realAspect / 0.5); // vs canon 1/2
      }

      setFacts({ ...dimensions, ...perceptual });
      setPhase('measured');
    };

    // Fase 1 — inmediata: imagen en caché → facts completos al montar
    const immediate = readImageSize(img);
    if (immediate.naturalWidth) {
      // Letterbox diferido (no bloquea la primera pintura estabilizada)
      if (letterbox) {
        const schedule = (window as unknown as { requestIdleCallback?: (cb: () => void) => void })
          .requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 0));
        schedule(() => {
          const samples = sampleEdges(img, letterboxBudgetMs);
          if (samples) {
            const lb = letterboxAnalyzer.analyze({
              items: [],
              signals: { edgeSamples: samples },
            }) as Partial<SubjectFacts>;
            setFacts((f) => ({ ...f, ...lb }));
          }
        });
      }
      settle(immediate);
      return;
    }

    // Fase 2 — onLoad delegado (el hook NO toca SmartMedia FROZEN)
    const onLoad = () => {
      const dims = readImageSize(img);
      if (!dims.naturalWidth) return; // onerror cae aquí → degradación (P7)
      if (letterbox) {
        const schedule = (window as unknown as { requestIdleCallback?: (cb: () => void) => void })
          .requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 0));
        schedule(() => {
          const samples = sampleEdges(img, letterboxBudgetMs);
          if (samples) {
            const lb = letterboxAnalyzer.analyze({
              items: [],
              signals: { edgeSamples: samples },
            }) as Partial<SubjectFacts>;
            setFacts((f) => ({ ...f, ...lb }));
          }
        });
      }
      settle(dims);
    };
    img.addEventListener('load', onLoad);
    img.addEventListener('error', onLoad); // error → readImageSize devuelve {} → baseline
    return () => {
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onLoad);
    };
  }, [items, containerRef, letterbox, letterboxBudgetMs]);

  return { facts, phase };
}

export default useMediaFacts;
